import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCurrency } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  CheckCircle,
  Sparkles,
  MessageSquare,
  Loader2,
  Rocket,
  TrendingUp,
  Crown,
  Monitor,
  Camera,
  Video,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  BOOTSTRAP_INTENSIVE,
  MONTHLY_PACKAGES,
  SOFTWARE_PLATFORM,
  getBootstrapPrice,
  getPackagePrice,
  getPackageFeatures,
} from "./pricing/pricingConfig";
import { FAQSchema, BreadcrumbSchema } from "../components/SchemaMarkup";

const PRICING_FAQS = [
  { question: "What is the Bootstrap Growth Intensive?", answer: "The Bootstrap Growth Intensive is a one-time mandatory setup fee required before starting any Growth Partnership. It includes a comprehensive business audit, ideal customer profiling, sales funnel mapping, CRM setup, analytics configuration, and AI workflow planning." },
  { question: "Can I switch between packages?", answer: "Yes, you can upgrade or downgrade your package at any time. We recommend starting with a package that fits your current needs and scaling up as your business grows." },
  { question: "What is included in the OptimaGrowth Software Platform?", answer: "The software platform includes pipeline management, WhatsApp integration, email & SMS automation, a landing page builder, workflow automation, AI chatbots, appointment booking, and a reporting dashboard. It's included with Momentum Builder and Scale Authority packages." },
  { question: "Do you work with international clients?", answer: "Yes, we serve clients in South Africa, the United Kingdom, and the United States. International clients receive all digital marketing services. Note that video and photo content production is only available for domestic (South African) clients." },
  { question: "Is there a minimum contract period?", answer: "The Starter / SEO Local package requires a minimum 3-month commitment. Growth Engine and above are month-to-month after an initial 3-month onboarding period to allow enough time to see measurable results." },
  { question: "How do I get started?", answer: "Simply choose a package and click 'Get a Quote'. Our team will reach out within 24 hours to schedule a discovery call and discuss your specific needs. You can also complete our Client Onboarding questionnaire at optimagrowth.agency/client-onboarding." },
];

const ADDITIONAL_SERVICES = [
  { name: "Advanced AI voice answering assistant", note: "Quoted per scope" },
  { name: "Custom chatbot builds", note: "Quoted per scope" },
  { name: "Additional video/photo production days", note: "Quoted per scope" },
  { name: "Additional post-production hours", note: "Quoted per scope" },
  { name: "Advanced SEO campaigns", note: "Quoted per scope" },
  { name: "Full brand development", note: "Quoted per scope" },
];

const getCardStyle = (pkg) => {
  if (pkg.id === "scale") return "bg-gradient-to-br from-optima-dark to-slate-800 text-white shadow-xl";
  if (pkg.popular) return "bg-white border-2 border-optima-blue shadow-blue";
  return "bg-white border border-slate-200 shadow-soft hover:border-optima-blue/30 transition-colors";
};

const getAccentColor = (pkg) => {
  if (pkg.id === "scale") return "optima-cyan";
  if (pkg.popular) return "optima-blue";
  return "optima-dark";
};

const getTextColor = (pkg, variant = "primary") => {
  const isElite = pkg.id === "scale";
  if (variant === "primary") return isElite ? "text-white" : "text-optima-dark";
  if (variant === "secondary") return isElite ? "text-white/80" : "text-slate-600";
  if (variant === "muted") return isElite ? "text-white/60" : "text-slate-400";
  return isElite ? "text-white/50" : "text-slate-500";
};

const getIncludesNoteStyle = (pkg) => {
  if (pkg.id === "scale") return "bg-optima-cyan/10 text-optima-cyan";
  if (pkg.popular) return "bg-optima-blue/5 text-optima-blue";
  return "bg-optima-dark/5 text-optima-dark";
};

const getButtonStyle = (pkg) => {
  if (pkg.id === "scale") return "bg-optima-cyan text-optima-dark hover:bg-optima-cyan/90";
  if (pkg.popular) return "bg-optima-blue hover:bg-optima-blue-dark";
  return "bg-optima-dark hover:bg-slate-800";
};

const PricingPage = () => {
  const { currency, setCurrency, currencies, formatPrice } = useCurrency();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const isInternational = currency !== "ZAR";

  const formatPriceRange = (range) => {
    const sym = currencies[currency].symbol;
    return `${sym}${range.min.toLocaleString()} – ${sym}${range.max.toLocaleString()}`;
  };

  const handleQuoteRequest = (pkg) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/quote`, {
        ...formData,
        package: selectedPackage.name,
        currency: currency,
      });
      toast.success(response.data.message);
      setIsModalOpen(false);
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="pt-32" data-testid="pricing-page">
      <SEO
        title="Pricing & Packages | SEO & Marketing Automation"
        description="Transparent pricing and packages for SEO, marketing automation, and growth services. Find the right OptimaGrowth plan for your business."
        keywords="marketing automation pricing, SEO packages South Africa, growth partnership cost, digital marketing retainer, lead generation pricing, CRM automation packages"
      />
      <FAQSchema faqs={PRICING_FAQS} />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Pricing" }]} />
      <Breadcrumb items={[{ name: "Pricing" }]} />

      {/* Hero Section */}
      <section className="section pb-8" data-testid="pricing-hero">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-optima-dark/10 text-optima-dark rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4 text-optima-cyan" />
                Growth Partnerships
              </span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                We Build Revenue Systems
              </h1>
              <p className="text-lg text-slate-600 mb-4 max-w-3xl mx-auto">
                OptimaGrowth partners with ambitious businesses to design, build and manage
                revenue-driven digital ecosystems that attract customers, convert consistently,
                and scale predictably.
              </p>
              <p className="text-base text-slate-500 mb-8">
                Start with the <strong className="text-optima-dark">Bootstrap Growth Intensive</strong>, then select a
                Growth Partnership tier that fits your business goals.
              </p>

              {/* Currency Selector */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-sm text-slate-500 font-medium">Currency:</span>
                <div className="flex bg-optima-dark rounded-full p-1 shadow-lg">
                  {Object.entries(currencies).map(([code, data]) => (
                    <button
                      key={code}
                      onClick={() => setCurrency(code)}
                      className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                        currency === code
                          ? "bg-optima-cyan text-optima-dark"
                          : "text-white/70 hover:text-white"
                      }`}
                      data-testid={`currency-btn-${code}`}
                    >
                      {data.symbol} {code}
                    </button>
                  ))}
                </div>
              </div>

              {isInternational && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-800 rounded-lg text-sm border border-amber-200"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Video/photo production excluded for international clients. All other services included.</span>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bootstrap Growth Intensive */}
      <section className="section pt-8" data-testid="bootstrap-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-gradient-to-br from-optima-dark to-slate-800 rounded-2xl p-8 lg:p-10 text-white">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-optima-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Rocket className="w-7 h-7 text-optima-cyan" />
                </div>
                <div>
                  <span className="text-optima-cyan text-sm font-medium uppercase tracking-wide">
                    Mandatory First Step
                  </span>
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold mt-1">
                    {BOOTSTRAP_INTENSIVE.name}
                  </h2>
                </div>
              </div>

              <p className="text-white/80 text-lg mb-6">{BOOTSTRAP_INTENSIVE.description}</p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold text-white mb-3">What's Included:</h4>
                  <ul className="space-y-2">
                    {BOOTSTRAP_INTENSIVE.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-white/80">
                        <CheckCircle className="w-5 h-5 text-optima-cyan flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col justify-between">
                  <div>
                    <h4 className="font-semibold text-white mb-3">Investment:</h4>
                    <div className="text-3xl lg:text-4xl font-heading font-bold text-optima-cyan mb-2">
                      {getBootstrapPrice(currency, formatPriceRange)}
                    </div>
                    <p className="text-white/60 text-sm">One-time setup fee</p>
                  </div>
                  <Button
                    onClick={() => handleQuoteRequest(BOOTSTRAP_INTENSIVE)}
                    className="mt-6 bg-optima-cyan text-optima-dark hover:bg-optima-cyan/90 font-semibold"
                    data-testid="bootstrap-quote-btn"
                  >
                    Get Started <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>

              <p className="text-white/60 text-sm border-t border-white/10 pt-4">
                {BOOTSTRAP_INTENSIVE.note}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Monthly Packages Header */}
      <section className="section pb-0" data-testid="packages-header">
        <div className="container-custom">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-optima-cyan/10 text-optima-cyan rounded-full text-sm font-medium mb-6">
              <TrendingUp className="w-4 h-4" />
              Monthly Growth Partnerships
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-4">
              Choose Your Growth Tier
            </h2>
            <p className="text-slate-600">
              Select the partnership level that matches your growth ambitions.
              All packages require completion of the Bootstrap Growth Intensive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Monthly Packages Grid */}
      <section className="section" data-testid="pricing-cards">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            {MONTHLY_PACKAGES.map((pkg, index) => {
              const Icon = pkg.icon;
              const features = getPackageFeatures(pkg, isInternational);
              const isElite = pkg.id === "scale";
              const accent = getAccentColor(pkg);

              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative rounded-2xl p-6 lg:p-8 ${getCardStyle(pkg)}`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  {pkg.popular && !isElite && (
                    <div className="absolute -top-4 left-6">
                      <span className="inline-flex items-center gap-1 px-4 py-1.5 bg-optima-blue text-white text-sm font-semibold rounded-full shadow-lg">
                        <Sparkles className="w-4 h-4" /> Most Popular
                      </span>
                    </div>
                  )}
                  {isElite && (
                    <div className="absolute -top-4 left-6">
                      <span className="inline-flex items-center gap-1 px-4 py-1.5 bg-optima-cyan text-optima-dark text-sm font-semibold rounded-full shadow-lg">
                        <Crown className="w-4 h-4" /> Elite Partner
                      </span>
                    </div>
                  )}

                  <div className="flex items-start gap-4 mb-4 mt-2">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-${accent}/10`}>
                      <Icon className={`w-6 h-6 text-${accent}`} />
                    </div>
                    <div>
                      <h3 className={`font-heading text-xl lg:text-2xl font-bold ${getTextColor(pkg)}`}>
                        {pkg.name}
                      </h3>
                      {pkg.subtitle && (
                        <span className="text-sm font-medium text-optima-cyan">{pkg.subtitle}</span>
                      )}
                    </div>
                  </div>

                  <p className={`text-sm mb-4 ${getTextColor(pkg, "secondary")}`}>{pkg.description}</p>
                  <p className={`text-xs mb-4 ${getTextColor(pkg, "muted")}`}>
                    <strong>Target:</strong> {pkg.target}
                  </p>

                  {pkg.includesNote && (
                    <p className={`text-xs font-medium mb-4 px-3 py-2 rounded-lg ${getIncludesNoteStyle(pkg)}`}>
                      {pkg.includesNote}
                    </p>
                  )}

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className={`font-heading text-2xl lg:text-3xl font-bold ${isElite ? "text-optima-cyan" : "text-optima-dark"}`}>
                        {getPackagePrice(pkg, currency, currencies)}
                      </span>
                      <span className={getTextColor(pkg, "muted")}>/month</span>
                    </div>
                    {pkg.commitment && (
                      <p className={`text-xs mt-1 ${getTextColor(pkg, "tertiary")}`}>{pkg.commitment}</p>
                    )}
                    {pkg.softwareIncluded && (
                      <p className="text-xs font-medium mt-1 text-optima-cyan">
                        OptimaGrowth Software included
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 mb-6">
                    {features.map((feature, i) => (
                      <div key={typeof feature === "string" ? feature : feature.text} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isElite ? "text-optima-cyan" : "text-green-500"}`} />
                        <span className={`text-sm ${getTextColor(pkg, "secondary")}`}>
                          {typeof feature === "string" ? feature : feature.text}
                        </span>
                      </div>
                    ))}
                    {pkg.notIncluded &&
                      pkg.notIncluded.map((item) => (
                        <div key={item} className="flex items-start gap-2 opacity-50">
                          <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${getTextColor(pkg, "muted")}`}>—</span>
                          <span className={`text-sm ${getTextColor(pkg, "tertiary")}`}>{item}</span>
                        </div>
                      ))}
                  </div>

                  {pkg.contentProduction && isInternational && (
                    <div className={`flex items-start gap-2 p-3 rounded-lg mb-6 text-sm ${isElite ? "bg-amber-500/20" : "bg-amber-50"}`}>
                      <Camera className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isElite ? "text-amber-300" : "text-amber-600"}`} />
                      <span className={isElite ? "text-amber-200" : "text-amber-800"}>
                        Content production not included for international clients
                      </span>
                    </div>
                  )}

                  {pkg.contentProduction && !isInternational && (
                    <div className={`flex items-start gap-2 p-3 rounded-lg mb-6 text-sm ${isElite ? "bg-green-500/20" : "bg-green-50"}`}>
                      <Video className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isElite ? "text-green-300" : "text-green-600"}`} />
                      <span className={isElite ? "text-green-200" : "text-green-800"}>{pkg.contentNote}</span>
                    </div>
                  )}

                  <Button
                    onClick={() => handleQuoteRequest(pkg)}
                    className={`w-full font-semibold ${getButtonStyle(pkg)}`}
                    data-testid={`quote-btn-${pkg.id}`}
                  >
                    Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OptimaGrowth Software Platform */}
      <section className="section-alt" data-testid="software-section">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-optima-dark to-slate-800 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-10 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-optima-cyan/20 flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-optima-cyan" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white">
                        {SOFTWARE_PLATFORM.name}
                      </h3>
                    </div>
                  </div>

                  <p className="text-white/80 mb-6">{SOFTWARE_PLATFORM.description}</p>

                  <div className="grid grid-cols-2 gap-3">
                    {SOFTWARE_PLATFORM.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-optima-cyan flex-shrink-0 mt-0.5" />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur p-8 lg:p-10 border-t lg:border-t-0 lg:border-l border-white/10">
                  <h4 className="font-semibold text-white mb-4">Platform Pricing:</h4>
                  <div className="space-y-4">
                    {SOFTWARE_PLATFORM.pricing.map((item) => (
                      <div
                        key={item.tier}
                        className="flex items-center justify-between pb-3 border-b border-white/10 last:border-0"
                      >
                        <span className="text-white/70 text-sm">{item.tier}</span>
                        <span className="font-semibold text-optima-cyan">
                          {item.price || `${formatPrice(item.priceZAR)}/mo`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-sm text-optima-cyan">
                    <Clock className="w-4 h-4" />
                    <span>{SOFTWARE_PLATFORM.trialNote}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section" data-testid="additional-services">
        <div className="container-custom">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-optima-pink/10 text-optima-pink rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Optional Add-Ons
            </div>
            <h2 className="font-heading text-3xl font-bold text-optima-dark mb-4">
              Additional Services
            </h2>
            <p className="text-slate-600">
              Expand your growth system with these optional services, quoted based on your specific requirements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {ADDITIONAL_SERVICES.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg border border-slate-200 p-4 hover:border-optima-blue/30 transition-colors"
                data-testid={`addon-${index}`}
              >
                <p className="text-slate-700 font-medium mb-1">{service.name}</p>
                <p className="text-xs text-slate-400">{service.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Notes Section */}
      <section className="section-alt" data-testid="pricing-notes">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl border border-slate-200 p-6 lg:p-8">
              <h3 className="font-heading text-xl font-bold text-optima-dark mb-4">
                Important Notes
              </h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-optima-blue flex-shrink-0 mt-0.5" />
                  <span>
                    Minimum <strong>3-month partnership commitment</strong> for all Growth Partnership tiers.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-optima-blue flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Bootstrap Growth Intensive</strong> is mandatory and must be completed before starting any monthly partnership.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-optima-blue flex-shrink-0 mt-0.5" />
                  <span>
                    For <strong>international clients (UK/USA)</strong>, content/video production is excluded, but all strategy, lead generation, automation, AI, and CRM services remain fully included.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-optima-blue flex-shrink-0 mt-0.5" />
                  <span>
                    All pricing is subject to final scope assessment during the Bootstrap Intensive phase.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Solution CTA */}
      <section className="section" data-testid="custom-cta">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-optima-blue to-optima-cyan rounded-2xl p-8 lg:p-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4">
                Not Sure Which Package Is Right?
              </h2>
              <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                Book a free growth audit and we'll help you identify the best starting point for your business goals.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
                data-testid="custom-quote-btn"
              >
                <MessageSquare className="w-5 h-5" />
                Book a Free Growth Audit
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

        {/* FAQ Section */}
        <section className="section" data-testid="pricing-faq">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark">
                  Frequently Asked Questions
                </h2>
                <p className="text-slate-600 mt-3">Common questions about our packages and pricing</p>
              </div>
              <div className="space-y-4">
                {PRICING_FAQS.map((faq, index) => (
                  <motion.details
                    key={faq.question}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="group bg-white border border-slate-200 rounded-xl p-5 cursor-pointer hover:border-optima-blue/30 transition-colors"
                    data-testid={`faq-item-${index}`}
                  >
                    <summary className="font-semibold text-optima-dark flex items-center justify-between list-none">
                      {faq.question}
                      <ArrowRight className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                    </summary>
                    <p className="text-slate-600 mt-3 text-sm leading-relaxed">{faq.answer}</p>
                  </motion.details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ChatGPT Ads CTA */}
        <section className="section" data-testid="chatgpt-ads-cta">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 md:p-12"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-optima-cyan/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-optima-blue/10 rounded-full blur-3xl" />
              <div className="relative grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-optima-cyan/10 text-optima-cyan text-xs font-bold uppercase tracking-wide mb-4">
                    <Sparkles className="w-3.5 h-3.5" /> New Service
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">
                    ChatGPT Ads + GEO + SEO
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    Be found everywhere your customers search — Google, ChatGPT, Perplexity, and beyond. One agency managing every AI and search channel.
                  </p>
                  <Link
                    to="/services/chatgpt-ads"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-optima-cyan text-slate-900 rounded-lg font-semibold hover:bg-optima-cyan/90 transition-colors"
                    data-testid="pricing-chatgpt-ads-link"
                  >
                    Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "ChatGPT Ads", desc: "OpenAI self-serve platform" },
                    { label: "GEO / AEO", desc: "AI answer visibility" },
                    { label: "SEO + Google Ads", desc: "Traditional search dominance" },
                    { label: "META Ads", desc: "Social advertising" },
                  ].map((item) => (
                    <div key={item.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <p className="text-white font-semibold text-sm">{item.label}</p>
                      <p className="text-white/50 text-xs mt-1">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Strategic Internal Links */}
        <section className="section bg-optima-dark">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="max-w-3xl mx-auto">
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">Ready to Start Growing?</h2>
              <p className="text-white/80 text-lg mb-8">Take the next step. Book a growth audit or explore more about how we help businesses scale.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors">
                  Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/client-onboarding" className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition-colors">
                  Start Client Onboarding
                </Link>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
                <Link to="/software" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Our Software Platform</Link>
                <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
                <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
              </div>
            </motion.div>
          </div>
        </section>

      {/* Quote Request Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="quote-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Get a Quote for {selectedPackage?.name}
            </DialogTitle>
            <DialogDescription>
              Fill out the form below and we'll send you a custom quote within 24 hours.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Input
                placeholder="Full Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-field"
                data-testid="quote-name-input"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Email Address *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="input-field"
                data-testid="quote-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Company Name"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="input-field"
                data-testid="quote-company-input"
              />
              <Input
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="input-field"
                data-testid="quote-phone-input"
              />
            </div>
            <div>
              <Textarea
                placeholder="Tell us about your business and goals..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="input-field min-h-[100px]"
                data-testid="quote-message-input"
              />
            </div>
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-slate-500">
                Selected: <strong>{selectedPackage?.name}</strong> ({currencies[currency].symbol})
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-optima-blue hover:bg-optima-blue-dark"
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Request Quote <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingPage;
